<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const languageToggleRef = ref(null);
const isSideNavOpen = ref(false);
const isScrolled = ref(false);
const isLangOpen = ref(false);
const expandedMenu = ref(null);

  const navLinks = ref([
  { to: '/', text: 'nav.links[0].text' },
  { to: '/training', text: 'nav.links[1].text', hasSubmenu: true },
  { to: '/expertise', text: 'nav.links[2].text' },
  { to: '/services', text: 'nav.links[3].text' },
  { to: '/ourBenefits', text: 'nav.links[4].text' },
  { to: '/companyPolicies', text: 'nav.links[7].text' },
  { to: '/contact', text: 'nav.links[5].text', class: 'contact-us' },
  { to: '/partner', text: 'nav.links[6].text' },
]);

const trainingSubmenu = ref([
  { to: '/habit-safe', text: 'Habit Safe' }
]);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', handleClickOutside);
});

const handleClickOutside = (event) => {
  if (languageToggleRef.value && !languageToggleRef.value.contains(event.target)) {
    isLangOpen.value = false;
  }
};

const onLinkClick = () => {
  isSideNavOpen.value = false;
  expandedMenu.value = null;
};

// <CHANGE> Toggle submenu function
const toggleSubmenu = (linkText) => {
  expandedMenu.value = expandedMenu.value === linkText ? null : linkText;
};
</script>

<template>
  <nav :class="{ 'scrolled-nav': isScrolled }">
    <router-link to="/" class="logo-link">
      <img alt="HLC logo" src="@/assets/new_logo_hlc.png" class="img-navlinks">
    </router-link>

    <div class="links">
      <template v-for="link in navLinks" :key="link.to">
        <div v-if="link.hasSubmenu" class="nav-item-with-submenu">
          <div class="nav-link-wrapper">
            <router-link :to="link.to" :class="['nav-link', link.class]">
              {{ $t(link.text) }}
            </router-link>
            <button 
              @click.stop="toggleSubmenu(link.text)" 
              class="submenu-toggle"
              :class="{ 'expanded': expandedMenu === link.text }"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <Transition name="submenu-fade">
            <div v-if="expandedMenu === link.text" class="submenu-dropdown">
              <router-link 
                v-for="subItem in trainingSubmenu" 
                :key="subItem.to" 
                :to="subItem.to" 
                class="submenu-link"
                @click="expandedMenu = subItem.to"
              >
                {{ subItem.text }}
              </router-link>
            </div>
          </Transition>
        </div>
        <router-link v-else :to="link.to" :class="['nav-link', link.class]">
          {{ $t(link.text) }}
        </router-link>
      </template>

      <div class="language-toggle" ref="languageToggleRef">
        <button @click="isLangOpen = !isLangOpen" class="lang-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
          {{ $i18n.locale.toUpperCase() }}
        </button>
        <Transition name="lang-fade">
          <div v-if="isLangOpen" class="lang-dropdown">
            <a @click="$i18n.locale = 'en'; isLangOpen = false" :class="{ active: $i18n.locale === 'en' }">English</a>
            <a @click="$i18n.locale = 'fr'; isLangOpen = false" :class="{ active: $i18n.locale === 'fr' }">Français</a>
          </div>
        </Transition>
      </div>
    </div>

    <button class="sidebar-toggle" @click="isSideNavOpen = !isSideNavOpen" aria-label="Toggle navigation">
      <span class="top-line" :class="{ open: isSideNavOpen }"></span>
      <span class="middle-line" :class="{ open: isSideNavOpen }"></span>
      <span class="bottom-line" :class="{ open: isSideNavOpen }"></span>
    </button>

    <Transition name="mobile-nav-backdrop">
      <div v-if="isSideNavOpen" class="mobile-nav-backdrop" @click="isSideNavOpen = false"></div>
    </Transition>
    <Transition name="mobile-nav-panel">
      <div v-if="isSideNavOpen" class="mobile-nav-panel">
        <div class="mobile-nav-content">
          <div class="mobile-nav-links">
            <template v-for="(link, index) in navLinks" :key="link.to">
              <div v-if="link.hasSubmenu" class="mobile-nav-item-with-submenu" :style="{ '--delay': `${index * 0.07}s` }">
                <div class="mobile-nav-link-wrapper">
                  <router-link :to="link.to" class="mobile-nav-link" @click="onLinkClick">
                    {{ $t(link.text) }}
                  </router-link>
                  <button 
                    @click.stop="toggleSubmenu(link.text)" 
                    class="mobile-submenu-toggle"
                    :class="{ 'expanded': expandedMenu === link.text }"
                  >
                    <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
                <Transition name="mobile-submenu">
                  <div v-if="expandedMenu === link.text" class="mobile-submenu">
                    <router-link 
                      v-for="subItem in trainingSubmenu" 
                      :key="subItem.to" 
                      :to="subItem.to" 
                      class="mobile-submenu-link"
                      @click="onLinkClick"
                    >
                      {{ subItem.text }}
                    </router-link>
                  </div>
                </Transition>
              </div>
              <router-link v-else :to="link.to" class="mobile-nav-link" @click="onLinkClick" :style="{ '--delay': `${index * 0.07}s` }">
                {{ $t(link.text) }}
              </router-link>
            </template>
          </div>
          <div class="lang-toggle-mobile" :style="{ '--delay': `${navLinks.length * 0.07}s` }">
            <button @click="$i18n.locale = 'en'; onLinkClick()" :class="{ active: $i18n.locale === 'en' }">English</button>
            <span class="lang-separator">|</span>
            <button @click="$i18n.locale = 'fr'; onLinkClick()" :class="{ active: $i18n.locale === 'fr' }">Français</button>
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

nav {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, rgba(10, 45, 97, 0.95) 0%, rgba(15, 60, 120, 0.95) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  width: 100%;
  height: 85px;
  padding: 0 5%;
  z-index: 100;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(252, 188, 13, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

nav.scrolled-nav {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 75px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.logo-link {
  display: flex;
  align-items: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-link:hover {
  transform: scale(1.05);
}

.img-navlinks {
  width: 60px;
  height: 60px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 8px rgba(252, 188, 13, 0.3));
}

.scrolled-nav .img-navlinks {
  width: 50px;
  height: 50px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
}

.nav-item-with-submenu {
  position: relative;
}

.nav-link-wrapper {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.nav-link {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.3px;
  padding: 0.5rem 0;
}

.scrolled-nav .nav-link {
  color: #1a1a2e;
}

.nav-link:hover {
  color: #fcbc0d;
  transform: translateY(-2px);
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #fcbc0d 0%, #ffd700 100%);
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 2px;
}

.nav-link:hover::after,
.nav-link.router-link-exact-active::after {
  width: 100%;
}

.nav-link.router-link-exact-active {
  color: #fcbc0d;
  font-weight: 600;
}

.scrolled-nav .nav-link.router-link-exact-active {
  color: #0A2D61;
}

.submenu-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.95);
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scrolled-nav .submenu-toggle {
  color: #1a1a2e;
}

.submenu-toggle:hover {
  color: #fcbc0d;
}

.submenu-toggle.expanded svg {
  transform: rotate(180deg);
}

.submenu-toggle svg {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.submenu-dropdown {
  position: absolute;
  top: calc(100% + 1rem);
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 200px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.submenu-link {
  display: block;
  padding: 0.875rem 1.25rem;
  color: #1a1a2e;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
}

.submenu-link:hover {
  background: linear-gradient(90deg, rgba(252, 188, 13, 0.1) 0%, transparent 100%);
  color: #0A2D61;
  border-left-color: #fcbc0d;
  padding-left: 1.5rem;
}

.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.submenu-fade-enter-from,
.submenu-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.contact-us {
  background: linear-gradient(135deg, #fcbc0d 0%, #ffd700 100%);
  color: #0A2D61 !important;
  padding: 0.75rem 1.75rem;
  border-radius: 50px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(252, 188, 13, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.contact-us:hover {
  background: linear-gradient(135deg, #ffd700 0%, #fcbc0d 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(252, 188, 13, 0.4);
}

.contact-us::after {
  display: none;
}

.language-toggle {
  position: relative;
}

.lang-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.625rem 1rem;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scrolled-nav .lang-button {
  color: #1a1a2e;
  background: rgba(10, 45, 97, 0.05);
  border-color: rgba(10, 45, 97, 0.1);
}

.lang-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.scrolled-nav .lang-button:hover {
  background: rgba(10, 45, 97, 0.1);
  border-color: rgba(10, 45, 97, 0.2);
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 110;
  min-width: 140px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.lang-dropdown a {
  display: block;
  padding: 0.875rem 1.25rem;
  color: #1a1a2e;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
}

.lang-dropdown a:hover {
  background: linear-gradient(90deg, rgba(252, 188, 13, 0.1) 0%, transparent 100%);
  color: #0A2D61;
  border-left-color: #fcbc0d;
  padding-left: 1.5rem;
}

.lang-dropdown a.active {
  background: linear-gradient(90deg, rgba(10, 45, 97, 0.1) 0%, transparent 100%);
  color: #0A2D61;
  font-weight: 600;
  border-left-color: #0A2D61;
}

.lang-fade-enter-active,
.lang-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.lang-fade-enter-from,
.lang-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.sidebar-toggle {
  display: none;
  position: relative;
  width: 30px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1100;
}

.sidebar-toggle span {
  display: block;
  position: absolute;
  height: 3px;
  width: 100%;
  background: #fff;
  border-radius: 3px;
  left: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.scrolled-nav .sidebar-toggle span {
  background: #0A2D61;
}

.sidebar-toggle .top-line { top: 0; }
.sidebar-toggle .middle-line { top: 50%; transform: translateY(-50%); }
.sidebar-toggle .bottom-line { bottom: 0; }

.sidebar-toggle .top-line.open {
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
}

.sidebar-toggle .middle-line.open {
  opacity: 0;
}

.sidebar-toggle .bottom-line.open {
  bottom: 50%;
  transform: translateY(50%) rotate(-45deg);
}

.mobile-nav-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 999;
}

.mobile-nav-backdrop-enter-active,
.mobile-nav-backdrop-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-nav-backdrop-enter-from,
.mobile-nav-backdrop-leave-to {
  opacity: 0;
}

.mobile-nav-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 85%;
  max-width: 380px;
  height: 100vh;
  background: linear-gradient(135deg, #0A2D61 0%, #0f3c78 100%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: -10px 0 50px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
}

.mobile-nav-panel-enter-active,
.mobile-nav-panel-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-nav-panel-enter-from,
.mobile-nav-panel-leave-to {
  transform: translateX(100%);
}

.mobile-nav-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem;
}

.mobile-nav-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 3rem;
  width: 100%;
}

.mobile-nav-item-with-submenu {
  width: 100%;
  opacity: 0;
  transform: translateY(20px);
  animation: mobileLinkFadeIn 0.5s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  animation-delay: calc(var(--delay) + 0.2s);
}

.mobile-nav-link-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mobile-nav-link {
  color: white;
  text-decoration: none;
  font-size: 1.25rem;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
  opacity: 0;
  transform: translateY(20px);
  animation: mobileLinkFadeIn 0.5s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  animation-delay: calc(var(--delay) + 0.2s);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
}

.mobile-nav-link:hover {
  color: #fcbc0d;
  background: rgba(252, 188, 13, 0.1);
}

.mobile-submenu-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  color: white;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-submenu-toggle:hover {
  color: #fcbc0d;
}

.mobile-submenu-toggle.expanded svg {
  transform: rotate(180deg);
}

.mobile-submenu-toggle svg {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-submenu {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 1rem;
  margin-top: 0.5rem;
}

.mobile-submenu-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  text-align: center;
}

.mobile-submenu-link:hover {
  color: #fcbc0d;
  background: rgba(252, 188, 13, 0.1);
}

.mobile-submenu-enter-active,
.mobile-submenu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-submenu-enter-from,
.mobile-submenu-leave-to {
  opacity: 0;
  max-height: 0;
}

.mobile-submenu-enter-to,
.mobile-submenu-leave-from {
  opacity: 1;
  max-height: 200px;
}

@keyframes mobileLinkFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lang-toggle-mobile {
  opacity: 0;
  transform: translateY(20px);
  animation: mobileLinkFadeIn 0.5s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  animation-delay: calc(var(--delay) + 0.2s);
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 2rem;
  margin-top: 1rem;
  gap: 1rem;
}

.lang-toggle-mobile button {
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.625rem 1.25rem;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
}

.lang-toggle-mobile button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.lang-toggle-mobile button.active {
  color: #fcbc0d;
  font-weight: 700;
  background: rgba(252, 188, 13, 0.15);
}

.lang-separator {
  color: rgba(255, 255, 255, 0.3);
  font-weight: 300;
}

@media screen and (max-width: 1024px) {
  .links {
    display: none;
  }
  .sidebar-toggle {
    display: block;
  }
}

@media screen and (max-width: 480px) {
  .mobile-nav-link {
    font-size: 1.1rem;
  }
  .mobile-nav-panel {
    width: 90%;
  }
  nav {
    padding: 0 4%;
  }
}
</style>